// 2026-08-19: lets Add/Edit Title accept a full YouTube or TMDB URL pasted into the
// trailer / Movie Database ID fields and reduces it down to just the ID/path those
// fields actually store. Both functions are safe no-ops on input that doesn't look
// like a URL of the relevant kind -- typing/pasting a bare ID or path directly still
// works exactly as before, untouched.

// Extracts the 11-character YouTube video ID from any of the common URL shapes
// (youtube.com/watch?v=, youtu.be/, youtube.com/embed/, youtube.com/shorts/, with or
// without www./m. and extra query params like &t= or &list=). Falls back to returning
// the (trimmed) input unchanged if it doesn't match a known YouTube URL pattern --
// covers someone typing/pasting a bare 11-char ID, or still-in-progress typing.
export function extractYoutubeId(input) {
    if (input === undefined || input === null) {
        return input;
    }

    const trimmed = input.trim();

    const patterns = [
        /(?:youtube\.com\/watch\?(?:.*&)?v=|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/shorts\/|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    ];

    for (const pattern of patterns) {
        const match = trimmed.match(pattern);
        if (match) {
            return match[1];
        }
    }

    return trimmed;
}

// Extracts the "tt1234567"-style IMDb id from a full IMDb URL
// (imdb.com/title/tt1234567/, with or without www., trailing slash, or query params).
// IMDb ids are tt + 7 digits classically, 8+ digits for titles issued since ~2019.
// Falls back to returning the (trimmed) input unchanged if it doesn't match an IMDb
// URL -- covers someone typing/pasting a bare tt-id directly, or still-in-progress
// typing.
export function extractImdbId(input) {
    if (input === undefined || input === null) {
        return input;
    }

    const trimmed = input.trim();

    const match = trimmed.match(/imdb\.com\/title\/(tt\d{7,})/);
    if (match) {
        return match[1];
    }

    return trimmed;
}

// Extracts the "/<country>/movie/<slug>" path from a full JustWatch URL
// (justwatch.com/us/movie/jackie-brown, with or without www., trailing slash, or query
// params). The stored path keeps the country segment as pasted -- JustWatch itself
// prompts visitors from other countries to switch locale, so the link stays useful
// worldwide. Falls back to returning the (trimmed) input unchanged if it doesn't match
// a JustWatch URL -- covers someone typing/pasting a bare "/us/movie/..." path
// directly, or still-in-progress typing.
export function extractJustwatchPath(input) {
    if (input === undefined || input === null) {
        return input;
    }

    const trimmed = input.trim();

    const match = trimmed.match(/justwatch\.com(\/[^?#\s]+)/);
    if (match) {
        // strip any trailing slash for consistency
        return match[1].replace(/\/$/, '');
    }

    return trimmed;
}

// Extracts the "/movie/<id>-<slug>" path from a full TMDB URL (themoviedb.org/movie/...,
// with or without www., a trailing slash, or query params). Falls back to returning the
// (trimmed) input unchanged if it doesn't match a TMDB URL -- covers someone
// typing/pasting a bare "/movie/..." path directly, or still-in-progress typing.
export function extractTmdbPath(input) {
    if (input === undefined || input === null) {
        return input;
    }

    const trimmed = input.trim();

    const match = trimmed.match(/(\/movie\/[a-zA-Z0-9-]+)/);
    if (match) {
        return match[1];
    }

    return trimmed;
}

// Extracts the season number from a full TMDB TV season URL
// (themoviedb.org/tv/1396-breaking-bad/season/3) or an already-extracted TV path with
// a /season/N suffix. Returns undefined if no /season/N segment is present -- covers
// a plain series URL/path with no season in it. 2026-08-20: added alongside
// extractTmdbTvPath so pasting a season-specific TMDB link can auto-select the Season
// dropdown instead of silently discarding which season was pasted.
export function extractTmdbSeasonNumber(input) {
    if (input === undefined || input === null) {
        return undefined;
    }

    const match = input.trim().match(/\/season\/(\d+)/);
    return match ? parseInt(match[1]) : undefined;
}

// Extracts the "/tv/<id>-<slug>" path from a full TMDB TV URL
// (themoviedb.org/tv/1396-breaking-bad, with or without www., a trailing slash, or
// query params -- also strips a trailing "/season/N" or similar TMDB TV sub-path,
// since the series id/slug is all this function needs to return). Falls back to
// returning the (trimmed) input unchanged if it doesn't match a TMDB TV URL --
// covers someone typing/pasting a bare "/tv/..." path directly, or still-in-progress
// typing. 2026-08-20: added for TV series support (movies use extractTmdbPath above).
export function extractTmdbTvPath(input) {
    if (input === undefined || input === null) {
        return input;
    }

    const trimmed = input.trim();

    const match = trimmed.match(/(\/tv\/[a-zA-Z0-9-]+)/);
    if (match) {
        return match[1];
    }

    return trimmed;
}
